<script setup lang="ts">
import { useQueryClient } from "@tanstack/vue-query"
import { useUrlSearchParams } from "@vueuse/core"
import { differenceInDays } from "date-fns"
import { computed, ref, watch, watchEffect } from "vue"
import { useRoute, useRouter } from "vue-router"
import { GET_EVENT_VAULT_QUERY_KEY, useGetEventVault } from "@/api"
import { DeleteEventModal, EventDeleteDrawer, EventExtendDrawer, EventLinksDrawer, FreemiumPlanDetails, InstallBanner, OnboardingGuideModal, SummaryEventDetailsCard, SummaryEventSettingsCard, SummaryEventStatsCard, ZipExportDrawer } from "@/components"
import { Banner, Button, ButtonLink } from "@/components/ui"
import { useToast } from "@/components/ui/ToastReka/useToast"
import { useConvertedRouteParam, useModal, useTranslation } from "@/composables"
import { SidebarHostEventLayout } from "@/layouts"
import { EventVaultStatus, ExperienceType, isEventWebsiteUrlModeEnabled } from "@/models"
import { resolveSlugPersistedPath } from "@/slugRoutes"
import { DeleteEventModalKey, EventDeleteDrawerKey, EventExtendDrawerKey, EventLinksDrawerKey, ZipExportDrawerKey } from "@/symbols"

definePage({
  meta: {
    requiresAuth: true,
  },
})

const { t } = useTranslation()
const route = useRoute()
const router = useRouter()
const queryClient = useQueryClient()
const toast = useToast()
const urlParams = useUrlSearchParams<{ source?: string, message?: string }>()
const eventVaultId = useConvertedRouteParam("id", "/event/[id]/", value => Number(value))

const { toggle: showLinks } = useModal(EventLinksDrawerKey)
const { toggle: showExtend } = useModal(EventExtendDrawerKey)
const { toggle: showDelete } = useModal(EventDeleteDrawerKey)
const { toggle: showConfirmDelete } = useModal(DeleteEventModalKey)
const { toggle: showExport } = useModal(ZipExportDrawerKey)
const isOnboardingGuideOpen = ref(false)

const { data: eventVault, isLoading, refetch, isFetching } = useGetEventVault(eventVaultId, { include: ["zip", "website"] })

const isSubscriptionEvent = computed<boolean>(() => Boolean(eventVault.value?.packageSubscriptionId))

const viewOnly = computed(() => {
  const status = eventVault.value?.status
  return status === EventVaultStatus.Archived
})

async function onZipRequested() {
  await refetch()
  await queryClient.invalidateQueries({ queryKey: [GET_EVENT_VAULT_QUERY_KEY] })
}

const isFreemiumEvent = computed<boolean>(() => eventVault.value?.freemiumMode ?? false)
const isEventSinglePage = computed<boolean>(() => eventVault.value?.eventVaultSettings?.experienceType === ExperienceType.SinglePage)

const hasLifecycleBanner = computed(() => {
  const status = eventVault.value?.status
  return status === EventVaultStatus.Expiring || status === EventVaultStatus.Expired
})

watchEffect(() => {
  if (urlParams.source && typeof urlParams.source === "string") {
    if (urlParams.source === "create" && !isLoading.value && isFreemiumEvent.value) {
      isOnboardingGuideOpen.value = true
      delete urlParams.source
    }
  }
})

watchEffect(() => {
  if (urlParams.message && typeof urlParams.message === "string") {
    toast.success({ description: urlParams.message })
    delete urlParams.message
  }
})

// When entering an event's context, apply the event's configured vanity slug to
// the URL once it's available (and not already present). The existing slug
// persistence machinery (AppLink + router beforeEach guard) then carries it
// across subsequent in-event navigation until the user leaves this event.
watch(() => eventVault.value?.website?.slug, (slug) => {
  if (!slug)
    return

  if (!isEventWebsiteUrlModeEnabled(eventVault.value?.eventVaultSettings?.eventWebsiteUrlMode))
    return

  const params = route.params as Record<string, string | string[] | undefined>
  const hasSlugParam = Array.isArray(params.slug)
    ? Boolean(params.slug[0])
    : Boolean(params.slug)
  if (hasSlugParam)
    return

  const prefixed = resolveSlugPersistedPath(
    { params: { slug } },
    { name: route.name ?? undefined, params, path: route.path, query: route.query, hash: route.hash },
  )
  if (prefixed) {
    router.replace({ ...prefixed, replace: true })
  }
}, { immediate: true })
</script>

<template>
  <InstallBanner />

  <SidebarHostEventLayout>
    <div class="flex flex-col gap-4">
      <div class="flex flex-col md:flex-row gap-4 flex-wrap justify-between">
        <h1 class="font-comfortaa font-bold text-xl">
          {{ t("summary_page.title") }}
        </h1>
        <div v-if="!viewOnly" class="flex gap-2 md:gap-4 flex-col md:flex-row flex-wrap">
          <ButtonLink v-if="eventVault?.eventVaultSettings?.invitationEnabled" :to="eventVault?.invitationLink" variant="tertiary" prefix-icon="Mail">
            {{ t("summary_page.view_invitation_btn") }}
          </ButtonLink>
          <ButtonLink :to="isEventSinglePage ? `/guest/${eventVaultId}` : `/guest/${eventVaultId}/gallery`" variant="tertiary" prefix-icon="ImageOn">
            {{ t("summary_page.view_gallery_btn") }}
          </ButtonLink>
          <ButtonLink :to="isEventSinglePage ? `/guest/${eventVaultId}?tab=guestbook` : `/guest/${eventVaultId}/messages`" variant="tertiary" prefix-icon="BookOpen">
            {{ t("summary_page.view_guestbook_btn") }}
          </ButtonLink>
        </div>
      </div>

      <Banner v-if="eventVault?.status === EventVaultStatus.Expiring" variant="warning" :title="t('summary_page.expiring_title')" :description="t('summary_page.expiring_description', { days: differenceInDays(eventVault?.expiryDate ?? new Date(), new Date()) })">
        <template #actions>
          <ButtonLink v-if="isSubscriptionEvent" variant="tertiary" href="https://guestpix.com/my-account/subscriptions/" target="_blank">
            {{ t("summary_page.manage_subscription_btn") }}
          </ButtonLink>
          <div v-else class="flex gap-2 w-full flex-wrap">
            <Button v-if="isFreemiumEvent" class="flex-1" variant="danger" @click="showDelete">
              {{ t("summary_page.download_delete_btn") }}
            </Button>
            <ButtonLink
              v-if="isFreemiumEvent && eventVault?.upgradeUrl"
              class="flex-1 bg-white"
              variant="tertiary"
              :href="eventVault.upgradeUrl"
            >
              {{ t("summary_page.freemium_banner.cta_mobile") }}
            </ButtonLink>
            <Button v-else class="flex-1 w-full bg-white" variant="tertiary" @click="showExtend">
              {{ t("summary_page.extend_plan_btn") }}
            </Button>
          </div>
        </template>
      </Banner>

      <Banner v-if="eventVault?.status === EventVaultStatus.Expired" variant="danger" :title="t('summary_page.expired_title')" :description="t('summary_page.expired_description')">
        <template #actions>
          <ButtonLink v-if="isSubscriptionEvent" variant="tertiary" href="https://guestpix.com/my-account/subscriptions/" target="_blank">
            {{ t("summary_page.manage_subscription_btn") }}
          </ButtonLink>
          <div v-else class="flex gap-2 w-full flex-wrap">
            <Button class="flex-1" variant="danger" @click="showDelete">
              {{ t("summary_page.download_delete_btn") }}
            </Button>
            <ButtonLink
              v-if="isFreemiumEvent && eventVault?.upgradeUrl"
              class="flex-1 bg-white"
              variant="tertiary"
              :href="eventVault.upgradeUrl"
            >
              {{ t("summary_page.freemium_banner.cta_mobile") }}
            </ButtonLink>
            <Button v-else class="flex-1 bg-white" variant="tertiary" @click="showExtend">
              {{ t("summary_page.extend_plan_btn") }}
            </Button>
          </div>
        </template>
      </Banner>

      <FreemiumPlanDetails
        v-if="isFreemiumEvent && eventVault && !hasLifecycleBanner"
        :event-vault="eventVault"
      />

      <div v-if="isLoading" class="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div class="gpx-skeleton w-full h-96" />
        <div class="gpx-skeleton w-full h-96" />
        <div class="gpx-skeleton w-full h-96" />
      </div>
      <div v-else-if="eventVault" :class="`grid grid-cols-1 gap-4 ${viewOnly ? 'xl:grid-cols-2' : 'xl:grid-cols-3'}`">
        <SummaryEventDetailsCard
          :event-vault="eventVault"
          :view-only="viewOnly"
        />
        <SummaryEventSettingsCard
          v-if="!viewOnly"
          :event-vault="eventVault"
          @show-links="showLinks"
          @show-extend="showExtend"
          @show-delete="showDelete"
          @show-export="showExport"
          @show-onboarding-guide="isOnboardingGuideOpen = true"
        />
        <SummaryEventStatsCard
          :event-vault="eventVault"
        />
      </div>
    </div>
  </SidebarHostEventLayout>

  <EventLinksDrawer :is-loading="isLoading" :event-vault="eventVault" />
  <EventExtendDrawer :is-loading="isLoading" :event-vault="eventVault" />
  <ZipExportDrawer :is-loading="isLoading || isFetching" :event-vault="eventVault" @zip-requested="onZipRequested" />
  <EventDeleteDrawer :is-loading="isLoading" :event-vault="eventVault" @delete-event="showConfirmDelete" @zip-requested="onZipRequested" />
  <DeleteEventModal :event-vault="eventVault" />
  <OnboardingGuideModal v-if="eventVault" v-model:open="isOnboardingGuideOpen" :event-vault="eventVault" />
</template>
